<template>
  <v-container :fluid="$vuetify.display.mdAndDown">
    <alert-card
        v-if="alert.visible === true"
        v-model:visible="alert.visible"
        :type="alert.type"
        :title="alert.title"
        :text="alert.text"
        :closable="false"
    />
    <v-layout
        justify-center
        align-center
    >
      <v-col>
        <car-request-admin-card-skeleton v-if="loadingCarRequest"/>
        <car-request-admin-card
            v-else
            :title="car.manufacturer + ', ' + car.model"
            :photos="car.photos"
            :manager-full-name="carRequest.managerFullName"
            :client-full-name="carRequest.clientFullName"
            :year="car.year"
            :engine="car.engine"
            :fuel="car.fuel"
            :transmission="car.transmission"
            :color="car.color"
            :mileage="car.mileage"
            :specification="car.specification"
            :states="states"
            v-model:state="calculation.state"
            v-model:price="calculation.price"
            v-model:delivery-price="calculation.deliveryPrice"
            v-model:paperwork-price="calculation.paperworkPrice"
            :total-price="calculation.totalPrice"
            :save-callback="saveRequest"
            :equipment-groups="specificationDetails"
            :description="description"
        />
        <car-request-attachment-card-skeleton
            v-if="loadingFiles"
            :is-client-view="false"
        />
        <car-request-attachment-card
            v-else
            :files="files"
            :is-disabled="false"
            :on-upload="uploadFiles"
            :on-download="downloadFile"
            :is-loading="uploadingFiles"
            :is-client-view="false"
        />
      </v-col>
    </v-layout>
  </v-container>
</template>

<script>

import {auth} from "@/common/auth";
import {router} from "@/router";
import {api} from "@/common/api";
import {inject} from "vue";
import CarRequestAttachmentCard from "@/components/CarRequest/CarRequestAttachmentCard.vue";
import {CarRequestStatus} from "@/common/enum/car_request_status";
import CarRequestAdminCard from "@/components/CarRequest/CarRequestAdminCard.vue";
import CarRequestAdminCardSkeleton from "@/components/CarRequest/CarRequestAdminCardSkeleton.vue";
import CarRequestAttachmentCardSkeleton from "@/components/CarRequest/CarRequestAttachmentCardSkeleton.vue";
import {dates} from "@/common/dates";
import AlertCard from "@/components/AlertCard.vue";
import {messages} from "@/common/enum/messages";
import {FileOwnerType} from "@/common/enum/file_owner_type";
import {Currency} from "@/common/enum/currency";
import {CONFIG} from "@/common/config";

export default {
  name: "AdminCarRequestView",
  components: {
    AlertCard,
    CarRequestAttachmentCardSkeleton, CarRequestAdminCardSkeleton, CarRequestAdminCard, CarRequestAttachmentCard},
  props: {
    id: String
  },
  data() {
    return {
      states: [],
      carRequest: {},
      car: {},
      calculation: {
        price: 0,
        deliveryPrice: 0,
        paperworkPrice: 0,
        totalPrice: 0
      },
      requestNotFinished: false,
      specificationDetails: {},
      description: null,
      files: [],
      loadingCarRequest: false,
      loadingFiles: false,
      uploadingFiles: false,
      alert: {
        visible: false,
        type: 'error',
        title: 'Error',
        text: ''
      }
    }
  },
  watch: {
    'calculation.price'() {
      this.calculation.totalPrice = parseFloat(this.calculation.price) + parseFloat(this.calculation.deliveryPrice) + parseFloat(this.calculation.paperworkPrice)
    },
    'calculation.deliveryPrice'() {
      this.calculation.totalPrice = parseFloat(this.calculation.price) + parseFloat(this.calculation.deliveryPrice) + parseFloat(this.calculation.paperworkPrice)
    },
    'calculation.paperworkPrice'() {
      this.calculation.totalPrice = parseFloat(this.calculation.price) + parseFloat(this.calculation.deliveryPrice) + parseFloat(this.calculation.paperworkPrice)
    }
  },
  mounted() {
    this.loadStates()
    this.getCarRequest()
    this.loadFiles()
  },
  setup() {
    return {
      switchAuthorizationState: inject('authorization').switchState
    }
  },
  methods: {
    loadStates() {
      this.states = Object.entries(CarRequestStatus).reduce((state, [key, value]) => {
        state.push({'value': key, 'title': value})
        return state
      }, [])
    },
    getCarRequest() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }
      this.loadingCarRequest = true
      api.getCarRequestById(token, this.id, this.setCarRequest)
    },
    setCarRequest(response) {
      if (response?.data?.status === 'error') {
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_order_view.order_loading_error : response.data.message
        return
      }

      this.loadingCarRequest = false
      this.alert.visible = false
      let carRequest = response.data
      this.carRequest.createdAt = dates.mapToDateTime(carRequest.created_at)
      if (carRequest.manager) {
        this.carRequest.managerFullName = carRequest.manager.profile.first_name + ' ' + carRequest.manager.profile.last_name
      } else {
        this.carRequest.managerFullName = 'Not assigned'
      }

      this.carRequest.clientFullName = carRequest.user.profile && carRequest.user.profile.firstName !== null && carRequest.user.profile.last_name !== null ? carRequest.user.profile.first_name + ' ' + carRequest.user.profile.last_name : carRequest.user.username

      this.car.id = carRequest.id
      this.car.manufacturer = carRequest.car.manufacturer.english_name
      this.car.model = carRequest.car.model.english_name
      this.car.photos = carRequest.car.car_photos.map(photo => {
        return CONFIG.API_HOST + '/api/v1/car-photos/' + photo.id
      })
      this.car.year = carRequest.car.year.value
      this.car.engine = carRequest.car.engine
      this.car.fuel = carRequest.car.fuel_type.english_name
      this.car.transmission = carRequest.car.transmission.english_name
      this.car.color = carRequest.car.color
      this.car.mileage = carRequest.car.mileage.value
      this.car.specification = carRequest.car.specification.english_name

      this.calculation.state = carRequest.last_status.state
      this.calculation.price = carRequest.car.public_prices.filter((currency) => currency.currency === Currency.USD)[0].value
      this.calculation.deliveryPrice = carRequest.last_status.delivery_price.value != 0 ? carRequest.last_status.delivery_price.value : 0
      this.calculation.paperworkPrice = carRequest.last_status.paper_work_price.value != 0 ? carRequest.last_status.paper_work_price.value : 0
      const totalCalculationAvailable = this.calculation.price && this.calculation.deliveryPrice && this.calculation.paperworkPrice
      this.calculation.totalPrice = totalCalculationAvailable ? parseFloat(this.calculation.price) + parseFloat(this.calculation.deliveryPrice) + parseFloat(this.calculation.paperworkPrice) : 0

      for (const index in carRequest.car.equipments) {
        const equipment = carRequest.car.equipments[index]
        const mappedEquipment = {
          englishName: equipment.english_name,
          isExists: equipment.is_exist
        }
        if (this.specificationDetails[equipment.equipment_group.english_name]) {
          this.specificationDetails[equipment.equipment_group.english_name].equipments.push(mappedEquipment)
          continue
        }

        this.specificationDetails[equipment.equipment_group.english_name] = {
          englishName: equipment.equipment_group.english_name,
          equipments: [mappedEquipment]
        }
      }

      this.description = carRequest.car.description
    },
    async saveRequest() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        await router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }
      let response = await api.createCarRequestStatus(token, this.id, {
        'state': this.calculation.state,
        'delivery_price': {
          'value': this.calculation.deliveryPrice,
          'currency': 'USD'
        },
        'paper_work_price': {
          'value': this.calculation.paperworkPrice,
          'currency': 'USD'
        },
      })
      this.setCarRequestStatus(response)
    },
    setCarRequestStatus(response) {
      if (response?.data?.status === 'error') {
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_order_view.calculation_saving_error : response.data.message
        return
      }

      this.alert.visible = false
      let carRequestStatus = response.data
      this.calculation.status = carRequestStatus.state
      this.calculation.deliveryPrice = carRequestStatus.delivery_price.value != 0 ? carRequestStatus.delivery_price.value : null
      this.calculation.paperworkPrice = carRequestStatus.paper_work_price.value != 0 ? carRequestStatus.paper_work_price.value : null
      const totalCalculationAvailable = !this.calculation.price && !this.calculation.deliveryPrice && !this.calculation.paperworkPrice
      this.calculation.totalPrice = totalCalculationAvailable ? this.calculation.price + this.calculation.deliveryPrice + this.calculation.paperworkPrice : null
    },
    uploadFiles(files) {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }
      this.uploadingFiles = true
      api.uploadFiles(token, this.id, {
        'files': files,
        'owner_type': FileOwnerType.manager
      }, this.loadFiles)
    },
    loadFiles() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.files.length === 0) {
        this.loadingFiles = true
      }
      api.loadFiles(token, this.id, this.setFiles)
    },
    setFiles(response) {
      this.loadingFiles = false
      this.uploadingFiles = false
      let attachments = response.data.attachments
      this.files = attachments.map((attachment) => {
        return {
          id: attachment.id,
          isClient: attachment.owner.user_type === FileOwnerType.client,
          filename: attachment.file_name,
          size: attachment.size,
          createdAt: dates.mapToDate(attachment.created_at)
        }
      })
    },
    async downloadFile(fileId) {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        await router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      let response = await api.downloadFile(token, fileId)
      this.loadFile(response)
    },
    loadFile(response) {
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers.filename);
      document.body.appendChild(link);
      link.click();
    }
  },
}
</script>

<style scoped>

</style>